import styles from "./ServiceStatus.module.scss";
import type { JSX } from "react";
import { type ContentType, type StepContent } from "../types";
import { CopyIcon, InfoIcon, LinkIcon } from "../assets/icons";
import { serviceConfig } from "../config/serviceConfig";
import { WarningIcon } from "../assets/icons/WarningIcon";
import { Card, InfoTooltip } from "@/shared/ui";
import { useServiceWorkflowStore } from "@/shared/stores/serviceWorkflowStore";

export interface TableStatusInfo {
  status: "Активный" | "Неактивный" | "Ожидает оплаты";
  subscriptionType: "free" | "paid" | "disabled";
  price: string;
  expiresAt: string;
  tableUrl: string;
  tableId: string;
  isOwner: boolean;
  isExpired: boolean;
  isExpiringSoon: boolean;
}

interface ServiceStatusProps {
  statusInfo: TableStatusInfo | null;
  onUpdate: () => void;
  onPay: () => void;
  onChangePayment: () => void;
  onCancel: () => void;
  onCopy: (value: string) => void;
  onDelete: (id: string) => void;
  onUnsubscribe: (id: string) => void;
}

export function ServiceStatus({
  statusInfo,
  onPay,
  onUpdate,
  onCopy,
  onDelete,
  onUnsubscribe,
}: ServiceStatusProps) {
  const { serviceType } = useServiceWorkflowStore();
  const config = serviceType ? serviceConfig[serviceType] : null;
  const steps = config?.service_steps;
  const servicePhoto = config?.photo;

  const renderMap: Record<
    ContentType,
    (block: StepContent, index: number) => JSX.Element
  > = {
    Text: (block, i) => (
      <div key={block.value} className={styles.stepWrap}>
        <p className={styles.stepText}>{block.value}</p>
        {i === 0 && (
          <div className={styles.iconWrap}>
            <InfoIcon className={styles.icon} />
          </div>
        )}
      </div>
    ),
    NoIconText: (block) => (
      <div key={block.value} className={styles.stepWrap}>
        <p className={styles.stepText}>{block.value}</p>
      </div>
    ),
    CopyField: (block) => (
      <div
        key={block.value}
        className={styles.stepWrap}
        onClick={() => onCopy(block.value || "")}
      >
        <span className={styles.copyValue}>{block.value}</span>
        <div className={styles.iconWrap}>
          <CopyIcon className={styles.icon} />
        </div>
      </div>
    ),
    LinkField: (block) => (
      <div
        key={block.value}
        className={styles.stepWrap}
        onClick={() => {
          if (block.value) {
            window.open(block.value, "_blank");
          }
        }}
      >
        <span className={styles.copyValue}>{block.value}</span>
        <div className={styles.iconWrap}>
          <LinkIcon className={styles.icon} />
        </div>
      </div>
    ),
  };

  return (
    <div className={styles.serviceStatusPage}>
      {statusInfo?.isExpiringSoon && (
        <div className={styles.warning}>
          <WarningIcon />
          <div>
            Подписка истекает ранее чем через сутки, не забудьте провести оплату
          </div>
        </div>
      )}
      <Card variant="narrow">
        <div className={styles.content}>
          <h2 className={styles.sectionMainTitle}>О сервисе</h2>
          <div
            className={`${styles.optionsList} ${
              serviceType === "auto_jam" ? styles.optionsListRow : ""
            }`}
          >
            <div>
              <div className={styles.optionsLabel}>Статус</div>
              <div
                className={`${styles.optionsValue} ${
                  statusInfo?.subscriptionType !== "disabled"
                    ? styles.greenStatus
                    : ""
                }`}
              >
                {statusInfo ? (
                  statusInfo.status
                ) : (
                  <div className={styles.spinner}></div>
                )}
              </div>
            </div>
            {statusInfo && serviceType !== "auto_jam" && (
              <div>
                <div className={styles.optionsLabel}>Тип подписки</div>
                <div className={styles.optionsValue}>
                  {statusInfo.subscriptionType === "free" ? (
                    <>
                      Бесплатная
                      <InfoTooltip text="Сейчас у вас бесплатная таблица за Р+. Оплатите сервис, чтобы получить полный функционал." />
                    </>
                  ) : statusInfo.subscriptionType === "paid" ? (
                    "Платная"
                  ) : (
                    "—"
                  )}
                </div>
              </div>
            )}
            <div>
              <div className={styles.optionsLabel}>
                {statusInfo?.subscriptionType === "free" &&
                serviceType !== "auto_jam"
                  ? "Цена апгрейда"
                  : "Цена"}
              </div>
              <div className={styles.optionsValue}>
                {statusInfo ? (
                  statusInfo.price
                ) : (
                  <div className={styles.spinner}></div>
                )}
              </div>
            </div>
            <div>
              <div className={styles.optionsLabel}>Окончание</div>
              <div className={styles.optionsValue}>
                {statusInfo ? (
                  statusInfo.subscriptionType === "free" ? (
                    "—"
                  ) : (
                    statusInfo.expiresAt
                  )
                ) : (
                  <div className={styles.spinner}></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
      <Card variant="narrow">
        <div className={styles.content}>
          <div className={styles.controlContent}>
            <h2 className={styles.sectionMainTitle}>Управление</h2>
            <div className={styles.sectionImage}>
              <img src={servicePhoto} alt="" />
            </div>
            <ol className={styles.stepList}>
              {steps?.map(({ index, content }) => (
                <li key={index} className={styles.stepItem}>
                  <span className={styles.stepNumber}>{index}</span>
                  <div className={styles.stepContent}>
                    {content.map((block: StepContent, i: number) =>
                      renderMap[block.type](block, i),
                    )}
                  </div>
                </li>
              ))}
            </ol>
            <h3 className={styles.sectionSubtitle}>Ссылка на таблицу:</h3>
            <div
              className={styles.stepWrap}
              onClick={() => {
                if (statusInfo?.tableUrl) {
                  window.open(statusInfo.tableUrl, "_blank");
                }
              }}
            >
              <span className={styles.copyValue}>
                {statusInfo ? (
                  statusInfo.tableUrl
                ) : (
                  <div className={styles.spinner}></div>
                )}
              </span>
              <div className={styles.iconWrap}>
                <LinkIcon className={styles.icon} />
              </div>
            </div>
            <div className={styles.serviceButtons}>
              {(statusInfo?.subscriptionType === "disabled" ||
                (statusInfo?.subscriptionType === "free" &&
                  serviceType !== "auto_jam") ||
                (statusInfo?.subscriptionType === "paid" &&
                  (statusInfo.isExpired || statusInfo.isExpiringSoon))) && (
                <button className={styles.primaryButton} onClick={onPay}>
                  Оплатить сервис
                </button>
              )}
              {statusInfo?.isOwner && (
                <>
                  {serviceType === "auto_jam" && (
                    <button className={styles.primaryButton} onClick={onUpdate}>
                      Изменить артикулы
                    </button>
                  )}
                  <button
                    onClick={() => onDelete(statusInfo.tableId)}
                    className={styles.cancelButton}
                  >
                    <div>Удалить таблицу</div>
                  </button>
                  {statusInfo.subscriptionType !== "disabled" && (
                    <button
                      onClick={() => onUnsubscribe(statusInfo.tableId)}
                      className={styles.cancelButton}
                    >
                      <div>Отключить подписку</div>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
